import * as THREE from 'three';
import { isNumeric } from 'mathjs';
import { isNumber } from './Misc';
import { ValueTypes } from '../../globals';
import { ByteUnits } from '../../interfaces/enums';
import { IColor, IRect, IVector2, IVector3, IVector4 } from '../../interfaces/Misc';

export default class MathUtils
{
	/**
     * determines if the given XY point is within the given rect's area
     * @static
     * @param {THREE.Vector2} point a coordinate in pixels
     * @param {IRect} rect defines a rectangular shape
     * @returns true if point is within the bounds of rect
     * @memberof MathUtils
    */
	static isPointWithinRect(point: THREE.Vector2, rect: IRect)
	{
		return point.x > rect.left &&
			point.x < rect.right &&
			point.y < rect.top &&
			point.y > rect.bottom;
	}

	/**
     * calculates the distance in pixels of the given XY point from the given rect's borders
     * @static
     * @param {IVector2} point a coordinate in pixels
     * @param {IRect} rect defines a rectangular shape
     * @returns sturct as { horizontal: number, vertical: number } indicating distance of point on each asix in pixels, or 0 if point is within the rect bounds. negative numbers mean the point is above or to the left of the rect.
     * @memberof MathUtils
    */
	static getPointDistanceFromRect(point: IVector2, rect: IRect)
	{
		return {
			horizontal: point.x > rect.left && point.x < rect.right ? 0 : point.x < rect.left ? point.x - rect.left : point.x - rect.right,
			vertical: point.y > rect.top && point.y < rect.bottom ? 0 : point.y < rect.top ? point.y - rect.top : point.y - rect.bottom,
		};
	}

	static getLineRectengaleIntersection(rect: IRect, lineStart: IVector2, lineEnd: IVector2)
	{
		//https://www.skytopia.com/project/articles/compsci/clipping.html (Liang-Barsky algorithm, tweaked to only return the first intersection)

		let t0 = 0, t1 = 1;
		const dx = lineEnd.x - lineStart.x;
		const dy = lineEnd.y - lineStart.y;
		let p, q, r;

		for(let edge = 0; edge < 4; edge++)
		{
			if (edge == 0) {  p = -dx;    q = -(rect.left - lineStart.x);  }
			if (edge == 1) {  p = dx;     q =  (rect.right - lineStart.x); }
			if (edge == 2) {  p = -dy;    q = -(rect.bottom - lineStart.y);}
			if (edge == 3) {  p = dy;     q =  (rect.top - lineStart.y);   }   

			r = q / p;

			if(p == 0 && q < 0) return null; 

			if(p < 0) 
			{
				if(r > t1) return null;  
				else if(r > t0) t0 = r;
			}
			else if(p > 0) 
			{
				if(r < t0) return null; 
				else if(r < t1) t1 = r;
			}
		}
		return {x: lineStart.x + t0 * dx, y: lineStart.y + t0 * dy}; 
	}		

	/**
     * @static
     * @param {IVector3} a
     * @param {IVector3} b
     * @param {number} [tolerance=0.00001]
     * @returns true if each member xyz of vector a is equal to its corresponding member in vector b within the given tolerance
     * @memberof MathUtils
     */
	static areVectorsEqual(a: IVector3, b: IVector3, tolerance = 0.00001)
	{
		return Math.abs(a.x - b.x) < tolerance && Math.abs(a.y - b.y) < tolerance && Math.abs(a.z - b.z) < tolerance;
	}


	/**
     * @static
     * @param {THREE.Quaternion} a
     * @param {THREE.Quaternion} b
     * @param {number} [tolerance=0.00001]
     * @returns true if each member xyzw of quaternion a is equal to its corresponding member in quaternion b within the given tolerance
     * @memberof MathUtils
     */
	static areQuaternionsEqual(a: IVector4, b: IVector4, tolerance = 0.00001)
	{
		return Math.abs(a.x - b.x) < tolerance && Math.abs(a.y - b.y) < tolerance && Math.abs(a.z - b.z) < tolerance && Math.abs(a.w - b.w) < tolerance;
	}


	static areColorsEqual(a: IColor, b: IColor, tolerance = 0.01)
	{
		return Math.abs(a.r - b.r) < tolerance && Math.abs(a.g - b.g) < tolerance && Math.abs(a.b - b.b) < tolerance;
	}

	/**
	 * clamps the given scalar value between min and max
	 * @static
	 * @param {Number} value the value to clamp
	 * @param {Number} min the lowest value possible
	 * @param {Number} max the highest value possible
	 * @returns the clamped value
	 * @memberof MathUtils
	*/
	static clampScalar(value: number, min: number, max: number)
	{
		return Math.max(min, Math.min(max, value));
	}

	/**
	 * clamps the given number betweeo 0 and 1
	 *
	 * @static
	 * @param {Number} number the number to clamp
	 * @returns the clamped number
	 * @memberof MathUtilsUtils
	*/
	static clamp01(number: number)
	{
		return Math.max(0, Math.min(1, number));
	}

	/**
	 * convert a value in range A to value in range B
	 *
	 * @static
	 * @param {Number} value the number in range A
	 * @param {Number} minA the min number in range A
	 * @param {Number} maxA the max number in range A
	 * @param {Number} minB the min number in range B
	 * @param {Number} maxB the max number in range B
	 * @returns the number value in range B
	 * @memberof MathUtils
	*/
	static convertFromRangeToRange(value: number, minA: number, maxA: number, minB: number, maxB: number)
	{
		return (((value - minA) * (maxB - minB)) / (maxA - minA)) + minB;
	}

	/**
	 * linearly interpolates between startingValue and targetValue according to interpolationTime, while taking valueType into account for extended type interpolation
	 * @static
	 * @param {Number | THREE.Vector2 | THREE.Vector3 | THREE.Color} startingValue what the value of the interpolation should be with interpolationTime = 0
	 * @param {Number | THREE.Vector2 | THREE.Vector3 | THREE.Color} targetValue what the value of the interpolation should be with interpolationTime = 1
	 * @param {Number} interpolationTime while this number should ideally be between 0-1, it will be clamped using MathUtils.clamp01 function
	 * @param {ValueTypes} valueType the type of value being interpolated, used to determine the correct interpolation function to use
	 * @returns the interpolated value
	 * @memberof MathUtils
	*/
	static lerpValue(startingValue: number | THREE.Vector2 | THREE.Vector3 | THREE.Color, 
		targetValue: number | THREE.Vector2 | THREE.Vector3 | THREE.Color, 
		interpolationTime: number, 
		valueType: string)
	{
		interpolationTime = MathUtils.clamp01(interpolationTime);

		switch (valueType)
		{
			case ValueTypes.Scalar:
				return THREE.MathUtils.lerp(startingValue as number, targetValue as number, interpolationTime);
			case ValueTypes.Vector2:
				return new THREE.Vector2().lerpVectors(startingValue as THREE.Vector2, targetValue as THREE.Vector2, interpolationTime);
			case ValueTypes.Vector3:
				return new THREE.Vector3().lerpVectors(startingValue as THREE.Vector3, targetValue as THREE.Vector3, interpolationTime);
			case ValueTypes.Color:
				return new THREE.Color().copy(startingValue as THREE.Color).lerp(targetValue as THREE.Color, interpolationTime);
			default:
				console.error("Unknown value type: " + valueType);
				break;
		}

		throw new TypeError("Type " + valueType + " is illegal! Use one of " + JSON.stringify(ValueTypes));
	}

	/**
     * rounds the given value with to the given decimals precision
     * @static
     * @param {Number | THREE.Vector3 | THREE.Quaternion} value
     * @param {number} [decimals=5]
     * @returns
     * @memberof MathUtils
     */
	static round(value, decimals = 5)
	{
		const precision = Math.pow(10.0, decimals);
		if (value.isVector3 || value.isEuler)
		{
			return value.set(Math.round(value.x * precision) / precision, Math.round(value.y * precision) / precision, Math.round(value.z * precision) / precision);
		}
		else if (value.isQuaternion)
		{
			return value.set(Math.round(value.x * precision) / precision, Math.round(value.y * precision) / precision, Math.round(value.z * precision) / precision, Math.round(value.w * precision) / precision);
		}
		else if (typeof value === 'number')
		{
			return Math.round(value * precision) / precision;
		}
	}

	/**
	 * formats the number in a way to display only the given number of decimals, rounding to the nearest value
	 * conserves scientific notaion
	 * e.g:
	 * - 1.00000001, 2 -> 1
	 * - 1.00000304e+47, 2 -> 1.00e+47
	 * - 1.99999999, 2 -> 2
	 *
	 * @static
	 * @param {Number} num the number to round
	 * @param {Number} decimals the number of decimals to display
	 * @returns {Number} the result of the rounded number
	 * @memberof MathUtils
	 */
		 static roundNumberDecimals(num: number, decimals: number)
		 {
			 const fixed1 = num.toFixed(decimals);
			 const parts = fixed1.split('e');
			 const fixed2 = Number(parts[0]).toFixed(decimals);
			 const result = Number(parts.length > 1 ? (fixed2 + "e" + parts[1]) : fixed2);
			 return result;
		 }

	/**
	 * makes sure the given number stays between from and to, wrapping around if necessary
	 *
	 * this differs from MathUtils.clampScalar in that the number will equal 'from' if it's too big, or 'to' if it's too low
	 * @static
	 * @param {Number} number the number to potentially wrap
	 * @param {Number} from the lowest value the number can be
	 * @param {Number} to the highest value the number can be
	 * @returns the wrapped number with 'from' as minimum and 'to' as maximum
	 * @memberof MathUtils
	 */
	static wrapNumber(number: number, from: number, to: number)
	{
		let result = number;
		const range = to - from;

		if (number < from)
		{
			result = to;// - (from - number);
		}
		else if (number > to)
		{
			result = from;// + (number - to);
		}

		if (result > range)
		{
			//result = wrapNumber(result, from, to);
		}

		return result;
	}

	/**
	 * checks if the given number x is a positive whole number using a neat regex trick
	 * - taken from http://stackoverflow.com/a/1019526/11236
	 * @static
	 * @param {Number} x te numnber to check
	 * @returns true if x is a positive integer; false otherwise
	 * @memberof MathUtils
	 */
	static isPositiveInteger(x: number)
	{
		return /^\d+$/.test(x.toString());
	}

	/**
	 *
	 *
	 * @static
	 * @param {*} quat
	 * @returns
	 * @memberof MathUtils
	 */
	static quaternionToAxisAngle(quat: THREE.Quaternion)
	{
		quat.normalize();
		const ret = {
			angle: (2 * Math.acos(quat.w)) * (180 / Math.PI),
			axis: { x: 0, y: 0, z: 0 },
		};
		if (1 - (quat.w * quat.w) < 0.000001)
		{
			ret.axis.x = quat.x;
			ret.axis.y = quat.y;
			ret.axis.z = quat.z;
		}
		else
		{
			const s = Math.sqrt(1 - (quat.w * quat.w));
			ret.axis.x = quat.x / s;
			ret.axis.y = quat.y / s;
			ret.axis.z = quat.z / s;
		}
		if (Math.abs(ret.axis.x) < 0.0001) ret.axis.x = 0.0;
		if (Math.abs(ret.axis.y) < 0.0001) ret.axis.y = 0.0;
		if (Math.abs(ret.axis.z) < 0.0001) ret.axis.z = 0.0;
		
		return ret;
	}
	
	static getShortestDeltaQuaternion(from: THREE.Quaternion, to: THREE.Quaternion)
	{
		const fromQuaternion = new THREE.Quaternion().copy(from).normalize();
		const toQuaternion = new THREE.Quaternion().copy(to).normalize();

		//insure that the shortest path is taken https://forum.unity.com/threads/shortest-rotation-between-two-quaternions.812346/
		if(fromQuaternion.dot(toQuaternion) < 0)
		{
			toQuaternion.x *= -1;
			toQuaternion.y *= -1;
			toQuaternion.z *= -1;
			toQuaternion.w *= -1;
		}

		return new THREE.Quaternion().multiplyQuaternions(fromQuaternion.invert(), toQuaternion).normalize();
	}

	/**
	* Rotates point around pivot by angle degrees on a 2D plane
	* @param {IVector2} pivot the center of rotation
	* @param {IVector2} point the point to rotate
	* @param {Number} angle the angle in degrees to rotate the point by, where positive is counterclockwise, and negative is clockwise
	* @returns {IVector2} the coordinates of the rotated point
	* @memberof MathUtils
	*/
	static rotatePointAroundPivot2D(pivot: IVector2, point: IVector2, angle: number)
	{
		angle *= Math.PI / 180;
	
		const cos = Math.cos(-angle);
		const sin = Math.sin(-angle);
	
		point.x -= pivot.x;
		point.y -= pivot.y;
	
		const xNew = point.x * cos - point.y * sin;
		const yNew = point.x * sin + point.y * cos;
	
		point.x = xNew + pivot.x;
		point.y = yNew + pivot.y;
	
		return { x: point.x, y: point.y };
	}

	/**
	 *
	 *
	 * @static
	 * @param {number} value
	 * @param {number} max
	 * @param {number} [min=0]
	 * @returns true if the min <= value <= max
	 * @memberof MathUtils
	 */
	static isNumberInRange(value: number, max: number, min = 0)
	{
		return value >= min && value <= max;
	}

	/**
	 * takes a number of bytes and formats them to the given unit type (B, KB, MB, GB, TB)
	 *
	 * @static
	 * @param {number} byteCount
	 * @param {ByteUnits} unit
	 * @returns
	 * @memberof MathUtils
	 */
	static formatByteSizeToUnit(byteCount: number, unit: ByteUnits)
	{
		if(!Array.isArray(byteCount) && !isNumber(byteCount)) return '0' + unit;

		const units = Object.values(ByteUnits);
		const unitIndex = units.indexOf(unit);
		if(unitIndex === -1) return '0' + unit;
		
		const unitSize = Math.pow(1024, unitIndex);
		const result = byteCount / unitSize;
		return result.toFixed(2) + unit;
	}

	/**
	 * Returns the input number formatted with the splitter string every 3 digits  
	 * for example: 1000000 -> "1,000,000"
	 *
	 * @static
	 * @param {number} value
	 * @param {string} [splitter=""]
	 * @return {*}  {string}
	 * @memberof TextUtils
	 */
	static formatBigNumber(value: number, splitter: string = ""): string
	{
		return value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, splitter);
	}

	/**
	 * Formats the given number of bytes into a human-readable string with the appropriate unit  
	 * for example: 1024 -> "1 KB", 1462536 -> "1.39 MB"
	 *
	 * @static
	 * @param {number} bytes
	 * @param {number} [decimals=2]
	 * @return {*} 
	 * @memberof TextUtils
	 */
	static formatBytes(bytes: number, decimals: number = 2): any
	{
		if (bytes === 0) return '0 Bytes';

		const k = 1024;
		const dm = decimals < 0 ? 0 : decimals;
		const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];

		const i = Math.floor(Math.log(bytes) / Math.log(k));

		return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
	}
}